import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import EditAdminForm from '../components/EditAdminForm';
import AdminProfileModal from '../components/AdminProfileModal';
import { useAuth } from '../context/AuthContext';
import { hasPermission } from '../config/permissions';

export default function AdminDetailsPage() {
    const { serverId, adminId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    
    const [admin, setAdmin] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
    
    const { userRole } = useAuth();
    const canEdit = hasPermission(userRole, 'CAN_EDIT_DATA');

    // 1. Check for the "Edit Signal" on load
    useEffect(() => {
        if (location.state?.openEditModal) {
            setIsEditModalOpen(true);
            window.history.replaceState({}, document.title);
        }
    }, [location]);

    // 2. Existing Data Fetching
    useEffect(() => {
        async function fetchAdmin() {
            try {
                const docRef = doc(db, 'servers', serverId, 'admins', adminId);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    setAdmin({ id: docSnap.id, ...docSnap.data() });
                } else {
                    console.error("No such admin!");
                }
            } catch (error) {
                console.error("Error fetching admin:", error);
            } finally {
                setLoading(false);
            }
        }
        fetchAdmin();
    }, [serverId, adminId]);

    if (loading) return <div style={{ padding: 20 }}>Loading Admin Dashboard...</div>;
    if (!admin) return <div style={{ padding: 20 }}>Admin not found.</div>;

    // --- DASHBOARD CARDS CONFIGURATION ---
    
    const dashboardCards = [
        { 
            title: "Visit Management", 
            icon: "📅", 
            desc: "Summary of visits associated with this admin.", 
            link: "visits",
            isVisible: true 
        },
        { 
            title: "Messages", 
            icon: "💬", 
            desc: "Internal messages from admins & employees.", 
            link: "messages",
            isVisible: false 
        },
        { 
            title: "Policies & Procedures", 
            icon: "📋", 
            desc: "Upload and manage compliance documents.", 
            link: "policies",
            isVisible: false 
        },
        { 
            title: "PPE Requests", 
            icon: "🦺", 
            desc: "Approve or reject equipment requests.", 
            link: "ppe-requests",
            isVisible: false 
        },
        { 
            title: "Device & Login History", 
            icon: "📱", 
            desc: "View device access logs and login history.", 
            link: "shared-access",
            isVisible: true 
        },
        { 
            title: "Geotagging", 
            icon: "📍", 
            desc: "Last known location and tracking.", 
            link: "geotagging",
            isVisible: true 
        }
    ];

    const ProfileHeader = () => (
        <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', marginBottom: '30px' }}>
            <button onClick={() => navigate(-1)} style={{ marginRight: '20px', padding: '8px 16px', border: '1px solid #ddd', borderRadius: '4px', background: 'white', cursor: 'pointer' }}>← Back</button>
            <img src={admin.profilePicture || 'https://via.placeholder.com/150'} alt="Profile" style={{ width: '80px', height: '80px', borderRadius: '50%', marginRight: '25px', objectFit: 'cover', border: '3px solid #f0f2f5' }} />
            <div style={{ flex: 1 }}>
                <h1 style={{ margin: '0 0 5px 0', fontSize: '24px', color: '#2c3e50' }}>{admin.fullName}</h1>
                <div style={{ display: 'flex', gap: '15px', color: '#666', fontSize: '14px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span>📧 {admin.email}</span>
                    <span>📞 {admin.phoneNumber || 'No Phone'}</span>
                    {admin.permissionLevels && admin.permissionLevels.length > 0 ? (
                        admin.permissionLevels.map((role, index) => (
                            <span key={index} style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold', backgroundColor: role === 'Super Admin' ? '#e6fffa' : '#ebf8ff', color: role === 'Super Admin' ? '#2c7a7b' : '#2b6cb0', border: `1px solid ${role === 'Super Admin' ? '#b2f5ea' : '#bee3f8'}` }}>{role}</span>
                        ))
                    ) : (
                        <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '12px', backgroundColor: '#f7fafc', color: '#718096' }}>Default</span>
                    )}
                </div>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => setIsProfileModalOpen(true)} style={{ padding: '10px 20px', backgroundColor: 'white', color: '#0056b3', border: '1px solid #0056b3', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>View Full Profile</button>
                {canEdit && (<button onClick={() => setIsEditModalOpen(true)} style={{ padding: '10px 20px', backgroundColor: '#0056b3', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>Edit Profile</button>)}
            </div>
        </div>
    );

    return (
        <div style={{ padding: '30px', backgroundColor: '#f4f6f8', minHeight: '100vh' }}>
            <ProfileHeader />
            <h2 style={{ color: '#4a5568', marginBottom: '20px', marginLeft: '5px' }}>Management Dashboard</h2>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
                {/* Dynamically filter out cards that are not visible yet */}
                {dashboardCards.filter(card => card.isVisible).map((card, index) => (
                    <div 
                        key={index}
                        onClick={() => {
                            if (card.link === "visits") {
                                navigate(`/servers/${serverId}/visits`, { state: { filterByAdmin: admin.username, adminName: admin.fullName } });
                            } else if (card.link === "geotagging") {
                                navigate(`/servers/${serverId}/admins/${adminId}/geotagging`);
                            } else if (card.link === "shared-access") {
                                navigate(`/servers/${serverId}/login-history/admin/${adminId}`, { state: { userName: admin.fullName } });
                            }
                        }}
                        style={{ backgroundColor: card.color || 'white', border: `1px solid ${card.borderColor || '#e2e8f0'}`, borderRadius: '10px', padding: '25px', cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s', boxShadow: '0 2px 4px rgba(0,0,0,0.02)', display: 'flex', flexDirection: 'column', gap: '10px' }}
                        onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 10px 15px rgba(0,0,0,0.1)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.02)'; }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}><span style={{ fontSize: '28px' }}>{card.icon}</span><span style={{ fontSize: '20px', color: '#cbd5e0' }}>→</span></div>
                        <h3 style={{ margin: '10px 0 5px', fontSize: '18px', color: '#2d3748' }}>{card.title}</h3>
                        <p style={{ margin: 0, fontSize: '13px', color: '#718096', lineHeight: '1.4' }}>{card.desc}</p>
                    </div>
                ))}
            </div>
            
            {isEditModalOpen && <EditAdminForm admin={admin} serverId={serverId} closeModal={() => setIsEditModalOpen(false)} onUpdate={() => window.location.reload()} />}
            {isProfileModalOpen && <AdminProfileModal admin={admin} closeModal={() => setIsProfileModalOpen(false)} />}
        </div>
    );
}